import { FC, useRef, useEffect, useLayoutEffect, useState, useCallback } from "react";
import { MenuProps } from "../types";
import { useStoreState } from "../store/useStore";
import { activeRouteStyles, stylesText } from "../styles";
import ArrowIcon from "@/nav-localized/assets/header/arrow.svg?react";
import { Languages, LanguagesType } from "../../types";
import { getCurrentPath } from "./getActiveClass";
import FuturesIconGif from "@/nav-localized/assets/header/future-logo.gif";
import clsx from "clsx";
import { MegaMenuPanel } from "./MegaMenu";

type AssetUrl = string | { src?: string; default?: string };
const getAssetUrl = (asset: AssetUrl) =>
  typeof asset === "string" ? asset : (asset.src ?? asset.default ?? "");
/** 关闭延迟（ms），鼠标从触发器移到面板的容差时间 */
const CLOSE_DELAY = 80;

const Menu: FC<{
  menus: MenuProps;
  changeRouter: (item: MenuProps, e: React.MouseEvent<HTMLAnchorElement>) => void;
  itemsKey?: string;
}> = ({ menus, changeRouter }) => {
  const { theme, localeLink, locale } = useStoreState();
  const isRTL = locale === "ar";
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [panelPos, setPanelPos] = useState<React.CSSProperties | null>(null);
  const [isActiveRoute, setIsActiveRoute] = useState(false);

  // ── hover 状态：控制二级面板的展开/收起 ──
  const [open, setOpen] = useState(false);

  const handleMouseEnter = useCallback(() => {
    // 进入时清除关闭定时器，立即打开
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    // 离开时延迟关闭（给鼠标从触发器移到面板留容差）
    closeTimer.current = setTimeout(() => {
      setOpen(false);
    }, CLOSE_DELAY);
  }, []);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  // 延迟激活状态检测，避免 SSR hydration 不匹配
  useEffect(() => {
    setIsActiveRoute(getCurrentPath(menus.activeRouts) || false);
  }, [menus.activeRouts]);

  // 动态计算下拉菜单定位，确保不溢出视口（同时兼容 LTR / RTL）
  // LTR 用 left 锚定（left 不随面板宽度变化）；RTL 用 right 锚定（right 不随面板宽度变化）
  const checkPosition = useCallback(() => {
    if (!containerRef.current || !dropdownRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const dropdownWidth = dropdownRef.current.offsetWidth || 0;
    const VIEWPORT_GUTTER = 16;
    const isRTL = getComputedStyle(containerRef.current).direction === "rtl";

    if (isRTL) {
      // RTL: 用 right 锚定，right 值不依赖 dropdownWidth → 宽度变化不需要重算
      // 默认 right=0（面板右边缘 = 容器右边缘）
      let rightOffset = 0;

      // 检查左边缘是否溢出视口
      const panelLeftEdge = containerRect.right - dropdownWidth;
      if (panelLeftEdge < VIEWPORT_GUTTER) {
        // 面板整体右移（right 取负值）
        rightOffset = panelLeftEdge - VIEWPORT_GUTTER;
      }

      // 检查右移后右边缘是否溢出视口
      const panelRightEdge = containerRect.right - rightOffset;
      if (panelRightEdge > window.innerWidth - VIEWPORT_GUTTER) {
        rightOffset = containerRect.right - window.innerWidth + VIEWPORT_GUTTER;
      }

      setPanelPos({ right: rightOffset });
    } else {
      // LTR: 用 left 锚定，left 值不依赖 dropdownWidth → 宽度变化不需要重算
      let desiredLeft = containerRect.left;
      const minLeft = VIEWPORT_GUTTER;
      const maxLeft = window.innerWidth - VIEWPORT_GUTTER - dropdownWidth;
      desiredLeft = Math.max(minLeft, Math.min(maxLeft, desiredLeft));

      setPanelPos({ left: desiredLeft - containerRect.left });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("resize", checkPosition);
    return () => window.removeEventListener("resize", checkPosition);
  }, [checkPosition]);

  // 监听面板尺寸变化（widget 列出现会改变宽度），自动重算位置
  useEffect(() => {
    if (!open || !dropdownRef.current) return;

    const observer = new ResizeObserver(() => {
      checkPosition();
    });
    observer.observe(dropdownRef.current);

    return () => observer.disconnect();
  }, [open, checkPosition]);

  // 打开时计算位置（在 paint 前完成，避免闪动；再用 rAF 确保拿到真实宽度）
  useLayoutEffect(() => {
    if (!open) return;
    // 首帧同步计算一次
    checkPosition();
    // 下一帧再算一次，防止字体/内容导致宽度延迟变化
    const raf = window.requestAnimationFrame(() => checkPosition());
    return () => window.cancelAnimationFrame(raf);
  }, [open, checkPosition]);

  const futuresIconSrc = getAssetUrl(FuturesIconGif as AssetUrl);

  return (
    <div
      ref={containerRef}
      className="flex items-center relative max-[860px]:h-[56px]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 一级触发器：始终可见 */}
      <span
        className={clsx(
          "h-[56px] flex items-center text-[14px] cursor-pointer",
          isActiveRoute && activeRouteStyles({ mode: theme }),
          stylesText({ mode: theme }),
          open
            ? theme === "dark"
              ? "text-dark-primary [&>svg>path]:fill-dark-primary"
              : "text-light-primary [&>svg>path]:fill-light-primary"
            : theme === "dark"
              ? "hover:text-dark-primary hover:[&>svg>path]:fill-dark-primary"
              : "hover:text-light-primary hover:[&>svg>path]:fill-light-primary"
        )}
      >
        {menus.recommend && futuresIconSrc && (
          <img src={futuresIconSrc} alt="futures" className="w-6 h-6 shrink-0 mr-[2px] -mt-2" />
        )}
        <span className="whitespace-nowrap">
          {
            Languages[locale as LanguagesType]?.header?.[
              menus.label as keyof (typeof Languages)[LanguagesType]["header"]
            ]
          }
        </span>
        <ArrowIcon
          className={clsx(
            "ml-[2px] transition-transform duration-200",
            open && "rotate-180",
            isActiveRoute && activeRouteStyles({ mode: theme }),
            !open && stylesText({ mode: theme }),
            open && (theme === "dark" ? "text-dark-primary" : "text-light-primary")
          )}
        />
      </span>

      {/* 二级展开面板：始终渲染，用 visible/opacity/translate 做 hover 动画（与生产一致） */}
      <MegaMenuPanel
        menus={menus}
        theme={theme}
        localeLink={localeLink}
        locale={locale}
        onNavigate={(item, e) => {
          window?.reportTrack?.("HeadingBar_Click", { tab_name: item.label });
          if (item.clickFunc) {
            item.clickFunc(e);
          } else {
            changeRouter(item, e);
          }
        }}
        open={open}
        className={clsx(
          "w-max max-w-[calc(100vw-32px)]",
          open
            ? "visible opacity-100 pointer-events-auto"
            : "invisible opacity-0 pointer-events-none"
        )}
        dropdownRef={dropdownRef}
        style={{
          ...(panelPos ?? { [isRTL ? "left" : "right"]: 0 }),
          transform: open ? "translateY(4px)" : "translateY(0)"
        }}
      />
    </div>
  );
};

export default Menu;
